import React, { useState, useCallback } from "react"
import NCTable from "../../shared/table/NCTable"
import NCText from "../../shared/NCText"
import {
  Grid, Box, IconButton, useTheme, useMediaQuery,
  Dialog, DialogTitle, DialogContent
} from "@material-ui/core"
import { useGetSubAdminFile, useGetSubAdmins } from "../../services/queries"
import { downloadFileFromResponse, formatDate } from "../../libs/utils"
import { SEARCH_CONFIG, TABLE_CONFIG } from "../../libs/config"
import NCInput from "../../shared/NCInput"
import NCButton from "../../shared/NCButton"
import ExportMenu from "../../components/ExportMenu"
import { AddIcon, DetailIcon, SearchIcon, CloseIcon } from "../../libs/icons"
import NCSvgIcon from "../../shared/NCSvgIcon"
import { useNavigate } from "react-router-dom"
import NCLoader from "../../shared/NCLoader"
import NCModal from "../../shared/NCModal"
import { toast } from "react-toastify"
import { useQueryClient } from "react-query"
import { useDeleteSubEmployee } from "../../services/mutations"
import useDebounce from "../../libs/hooks/Debounce"
import NotificationAlert from "../../components/notification/NotificationAlert"
import { useTranslation } from "react-i18next"
import { QRCodeCanvas } from "qrcode.react"
import QRCode from "qrcode"
import QrCode2Icon from "@mui/icons-material/QrCode2"

const SubAdminList = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1)
  const [query, setQuery] = useState("")
  const [pageSize, setPageSize] = useState(TABLE_CONFIG.PAGE_SIZE)
  const [ordering, setOrdering] = useState("-created")
  const [statusFilter, setStatusFilter] = useState([])
  const [downloadType, setDownloadType] = useState()
  const [showDeleteModel, setShowDeleteModel] = useState(false)
  const [subAdminId] = useState()
  const [qrModalData, setQrModalData] = useState(null)
  const debouncedSearchTerm = useDebounce(query, SEARCH_CONFIG.TIME_IN_MILLI)
  const nav = useNavigate()

  const handleDownloadQR = useCallback(async () => {
    if (!qrModalData?.uuid) return
    try {
      const canvas = document.createElement("canvas")
      await QRCode.toCanvas(canvas, `${window.location.origin}/school/${qrModalData.uuid}`, {
        width: 1000,
        margin: 2,
      })
      const url = canvas.toDataURL("image/png")
      const link = document.createElement("a")
      link.download = `${qrModalData?.name || "school"}-qr.png`
      link.href = url
      link.click()
      toast.success("QR code downloaded")
    } catch {
      toast.error("Failed to generate QR code")
    }
  }, [qrModalData])

  const deleteSubEmployee = useDeleteSubEmployee()
  const queryClient = useQueryClient()

  const mobileTheme = useTheme()
  const isSmallScreen = useMediaQuery(mobileTheme.breakpoints.down("sm"))

  const { isLoading, data } = useGetSubAdmins(
    page,
    debouncedSearchTerm,
    pageSize,
    {
      ordering,
      status:
        statusFilter && statusFilter.length
          ? statusFilter[0].value.toString()
          : null
    }
  )
  const { refetch } = useGetSubAdminFile(
    downloadType,
    query,
    { ordering },
    {
      enabled: false
    }
  )

  const {
    next,
    previous,
    total_pages,
    results: clientData
  } = data?.data?.data || {}

  const onNext = () => {
    setPage(prev => (next ? prev + 1 : prev))
  }
  const onPrev = () => {
    setPage(prev => (previous ? prev - 1 : prev))
  }
  const deleteSubAdmin = clientId => {
    deleteSubEmployee.mutate(clientId, {
      onSuccess: () => {
        toast.success(t("subadmin.deleted"))
        queryClient.invalidateQueries(["getSubadminList"])
        setShowDeleteModel(false)
      }
    })
  }

  const getSubadminByName = cell => {
    return (
      <div onClick={() => nav(`/subadmins/${cell.id}`)}>
        <NCText className={"clickable-text"}>{cell?.name}</NCText>
      </div>
    )
  }

  const getActionColumn = cell => {
    return (
      <div className="action-icons">
        {cell?.uuid && (
          <IconButton onClick={() => setQrModalData(cell)} title="Show QR Code">
            <QrCode2Icon />
          </IconButton>
        )}
        <IconButton onClick={() => nav(`/subadmins/${cell.id}`)}>
          <DetailIcon />
        </IconButton>
      </div>
    )
  }

  const columns = [
    {
      Header: t("list.school_id"),
      id: "id",
      accessor: cell => cell.id,
      Cell: ({ value }) => (
        <NCText
          className={"clickable-text"}
          onClick={() => nav(`/subadmins/${value}`)}
        >
          {value}
        </NCText>
      ),
      disableSortBy: false
    },
    {
      Header: t("list.name"),
      id: "name",
      accessor: cell => getSubadminByName(cell)
    },
    {
      Header: t("container.admin.dist_name"),
      accessor: "admin_name"
    },

    {
      Header: t("list.date_created"),
      id: "created",
      accessor: cell => cell.created,
      Cell: ({ value }) => formatDate(value)
    },
    {
      Header: t("tol_clients"),
      accessor: "total_clients",
      Cell: ({ value, row }) => (
        value ? <NCText
          className={"clickable-text"}
          onClick={() => nav(`/clients/?subadmin=${row?.original?.id}`)}
        >
          {value}
        </NCText> : value
      ),
    },
    {
      Header: "",
      id: "action",
      accessor: cell => getActionColumn(cell),
      disableSortBy: true
    }
  ]
  const downloadFile = async type => {
    await setDownloadType(type)
    const data = await refetch()
    const contentDispositionHeaders =
      data?.data?.headers["content-disposition"].split("=")
    downloadFileFromResponse(
      data?.data?.data,
      contentDispositionHeaders[contentDispositionHeaders.length - 1]
        .replaceAll(/[/"]/g, "")
        .trim()
    )
  }
  return (
    <Grid container>
      <Grid item xs={12}>
        <Box mb={2}>
          <Grid container justifyContent="space-between" mb={20}>
            <Grid item xs={12} md={4}>
              {!isSmallScreen ? (
                <NCText variant={"h5"}>{t("subadmin.heading")}</NCText>
              ) : (
                <Grid container justifyContent="space-between">
                  <Grid item style={{ paddingLeft: "0" }}>
                    <NCText variant={"h5"}>{t("subadmin.heading")}</NCText>
                  </Grid>
                  <Grid item style={{ paddingLeft: "0" }}>
                    <NotificationAlert />
                  </Grid>
                </Grid>
              )}
            </Grid>
            <Grid item xs={12} md={8}>
              <Grid container spacing={2} justifyContent={isSmallScreen ? "flex-start" : "flex-end"}>
                <Grid item>
                  <NCInput
                    value={query}
                    onChange={e => {
                      setPage(1)
                      setOrdering(null)
                      setQuery(e.target.value)
                    }}
                    placeholder={t("common.search_here")}
                    startIcon={SearchIcon}
                    className="rounded"
                  />
                </Grid>
                <Grid item>
                  <ExportMenu itemAction={item => downloadFile(item.key)} />
                </Grid>
                <Grid item style={{ paddingRight: "0px" }}>
                  <NCButton
                    variant="contained"
                    color="primary"
                    endIcon={<NCSvgIcon component={AddIcon} inheritViewBox />}
                    onClick={() => nav("/subadmins/add")}
                  >
                    {t("subadmin.add_subadmin")}
                  </NCButton>
                </Grid>
                {!isSmallScreen && (
                  <Grid item style={{ paddingLeft: "0" }}>
                    <NotificationAlert />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <NCTable
          columns={columns}
          data={clientData || []}
          page={page}
          onNext={onNext}
          onPrev={onPrev}
          pageCount={total_pages}
          gotoPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          sorting={ordering}
          setSorting={setOrdering}
          filter={statusFilter}
          setFilter={setStatusFilter}
          isLoading={isLoading}
        />
        {isLoading && <NCLoader open={true} />}
      </Grid>
      {/* QR Code Modal */}
      <Dialog
        open={!!qrModalData}
        onClose={() => setQrModalData(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <NCText style={{ fontWeight: 700, fontSize: 17 }}>
              {qrModalData?.name} - QR Code
            </NCText>
            <IconButton size="small" onClick={() => setQrModalData(null)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" gridGap={16} py={1}>
            <Box p={1.5} style={{ border: "1px solid #E5E7EB", borderRadius: 12 }}>
              {qrModalData?.uuid && (
                <QRCodeCanvas
                  value={`${window.location.origin}/school/${qrModalData.uuid}`}
                  size={1000}
                  style={{ width: "100%", maxWidth: 300, height: "auto" }}
                />
              )}
            </Box>
            <Box display="flex" gridGap={12} width="100%" justifyContent="center">
              <NCButton
                variant="contained"
                color="primary"
                onClick={handleDownloadQR}
                style={{ flex: 1, maxWidth: 140 }}
              >
                Download QR
              </NCButton>
              <NCButton
                variant="outlined"
                color="primary"
                onClick={() => window.open(`/school/${qrModalData?.uuid}`, "_blank")}
                style={{ flex: 1, maxWidth: 140 }}
              >
                Tracking Rides
              </NCButton>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <NCModal
        open={showDeleteModel}
        title={t("common.delete")}
        text={t("subadmin.delete_confirmation")}
        onClose={() => setShowDeleteModel(false)}
        submitButton={
          <NCButton
            variant="contained"
            color="secondary"
            disableElevation
            onClick={() => deleteSubAdmin(subAdminId)}
            style={{ width: "auto" }}
          >
            {t("common.yes")}
          </NCButton>
        }
      />
    </Grid>
  )
}
export default SubAdminList
